import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const MarcellaContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ProfileSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProfileImage = styled.div`
  background: url('/assets/Marcella.jpg') center/cover;
  border-radius: 20px;
  aspect-ratio: 3/4;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadows.card};
`;

const ProfileContent = styled.div`
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
  }
`;

const QualificationsSection = styled.div`
  margin-top: 3rem;
`;

const QualificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QualificationCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: ${props => props.theme.shadows.sm};
  text-align: center;
  
  &::before {
    content: '✓';
    color: ${props => props.theme.colors.primary};
    font-weight: bold;
    font-size: 1.5rem;
    display: block;
    margin-bottom: 1rem;
  }
  
  strong {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
  }
`;

const Marcella = () => {
  return (
    <MarcellaContainer>
      <Helmet>
        <title>Marcella Deeken - Der Ponystall | Tiergestützte Pädagogik</title>
        <meta name="description" content="Lernen Sie Marcella Deeken kennen - Ihre Expertin für tiergestützte Pädagogik und heilpädagogisches Reiten mit langjähriger Erfahrung." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Marcella Deeken
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ProfileSection>
            <ProfileImage />
            <ProfileContent>
              <h2>Über mich</h2>
              <p>
                Ich bin Marcella Deeken (geb. 24.04.1974), verheiratet und Mutter von vier Kindern. 
                Die Arbeit mit Ponys und Menschen ist für mich mehr als ein Beruf – sie ist meine 
                Herzensaufgabe. In der tiergestützten Pädagogik erlebe ich jeden Tag, wie sehr Tiere 
                Vertrauen stärken, Achtsamkeit fördern und kleine wie große Entwicklungsschritte 
                anstoßen können.
              </p>
              <p>
                Aus meiner langjährigen Tätigkeit in der pädagogischen Arbeit bringe ich viel Erfahrung in der Begleitung von Kindern und 
                Familien mit. Was mich auszeichnet, sind Organisationstalent, Teamfähigkeit und die 
                Freude daran, lösungs- und zielorientiert zu planen und zu handeln – immer mit einem 
                offenen, freundlichen Wesen und einer positiven, humanitären Haltung.
              </p>
              
            </ProfileContent>
          </ProfileSection>
          
          <QualificationsSection>
            <h2 style={{ textAlign: 'center', marginBottom: '1rem', color: '#333' }}>Meine Qualifikationen</h2>
            <QualificationsGrid>
              <QualificationCard>
                <strong>Staatlich anerkannte Erzieherin</strong><br />
                mit heilpädagogischer Zusatzausbildung (Integration)
              </QualificationCard>
              <QualificationCard>
                <strong>Fachkraft für tiergestützte Therapie und Förderung</strong>
              </QualificationCard>
              <QualificationCard>
                <strong>Reitpädagogin</strong><br />
                mit Schwerpunkt motopädagogische und psychomotorische Förderung
              </QualificationCard>
              <QualificationCard>
                <strong>Langjährige pädagogische Arbeit</strong><br />
                mit Kindern und Jugendlichen
              </QualificationCard>
              <QualificationCard>
                <strong>Integrative Erziehung und Bildung</strong><br />
                mit Offener Arbeit und Situationsorientiertem Ansatz
              </QualificationCard>
              <QualificationCard>
                <strong>Wald- und Naturpädagogik</strong><br />
                sowie Erlebnispädagogik
              </QualificationCard>
              <QualificationCard>
                <strong>Basispass, Longierabzeichen</strong><br />
                und Physiotherapie am Pferd
              </QualificationCard>
            </QualificationsGrid>
          </QualificationsSection>
        </Container>
      </ContentSection>
    </MarcellaContainer>
  );
};

export default Marcella;
